import React, { useState } from 'react';
import { X, Play, Pause, Cloud, Loader2 } from 'lucide-react';
import services from '../../../API';

const MusicLibraryModal = ({ isOpen, onClose, onGenerate, onAddFromDrive, onSelectTrack, videoAdId, videoDuration }) => {
    const [prompt, setPrompt] = useState('');
    const [duration, setDuration] = useState(Math.ceil(videoDuration || 30));
    const [isCustomDuration, setIsCustomDuration] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [optimizePrompt, setOptimizePrompt] = useState(true);
    const [selectedGenre, setSelectedGenre] = useState('Dance');
    const [selectedMood, setSelectedMood] = useState('Relaxed');
    const [assets, setAssets] = useState([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    const [playingTrackId, setPlayingTrackId] = useState(null);
    const [loadingTrackId, setLoadingTrackId] = useState(null);
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.onended = () => {
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        };
        audioRef.current.onplaying = () => setLoadingTrackId(null);
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const togglePlay = (e, track) => {
        e.stopPropagation();
        if (playingTrackId === track._id || loadingTrackId === track._id) {
            audioRef.current.pause();
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        } else {
            if (audioRef.current) {
                setLoadingTrackId(track._id);
                setPlayingTrackId(track._id);
                audioRef.current.src = track.url;
                audioRef.current.play().catch(err => {
                    console.error("Playback failed:", err);
                    setPlayingTrackId(null);
                    setLoadingTrackId(null);
                });
            }
        }
    };

    React.useEffect(() => {
        const fetchAssets = async () => {
            setIsLoadingAssets(true);
            try {
                // Fetch standard assets
                const response = await services.getAssets();
                let audioAssets = [];
                if (response?.data?.assets) {
                    audioAssets = response.data.assets.filter(asset =>
                        asset.type === 'audio' ||
                        asset.mimeType?.startsWith('audio/') ||
                        ['mp3', 'wav', 'ogg', 'm4a'].some(ext => asset.name?.toLowerCase().endsWith(ext))
                    );
                }

                setAssets(audioAssets);
            } catch (error) {
                console.error("Error fetching assets:", error);
            } finally {
                setIsLoadingAssets(false);
            }
        };

        if (isOpen) {
            fetchAssets();
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (isOpen && !isCustomDuration) {
            setDuration(Math.ceil(videoDuration || 30));
        }
    }, [isOpen, videoDuration, isCustomDuration]);

    if (!isOpen) return null;

    const handleGenerate = async () => {
        if (!prompt) return;
        setIsGenerating(true);
        try {
            const lengthMs = duration * 1000;
            const payload = {
                prompt: prompt,
                musicLengthMs: lengthMs
            };
            const response = await services.generateMusic(payload);
            if (onGenerate) {
                onGenerate(response.data);
                onClose(); // Auto-close after successful generation
            }
        } catch (error) {
            console.error("Error generating music:", error);
        } finally {
            setIsGenerating(false);
        }
    };

    const genres = ['Pop', 'Rock', 'Hip Hop', 'R&B', 'Electronic', 'Dance', 'Classical', 'Jazz', 'Folk', 'Country'];
    const moods = ['Uplifting', 'Happy', 'Sad', 'Energetic', 'Chill', 'Melancholic', 'Hopeful', 'Dreamy', 'Romantic', 'Dark', 'Aggressive', 'Peaceful', 'Mysterious', 'Epic', 'Introspective', 'Euphoric', 'Nostalgic', 'Playful', 'Tense', 'Relaxed'];



    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-white dark:bg-[#111111] w-full max-w-6xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-white/10">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/5">
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-tight">Generate Music</h1>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-8">
                    {/* Generation Section */}
                    <div className="bg-gray-50 dark:bg-[#191919] rounded-2xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                        <div className="flex gap-6">
                            <div className="flex-1 space-y-4">
                                <textarea
                                    className="w-full bg-white dark:bg-black border border-gray-200 dark:border-white/10 rounded-xl p-4 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-gray-900 dark:text-white"
                                    placeholder="Describe the music you want to generate (e.g., 'Upbeat electronic music with a modern feel, perfect for a tech product launch')"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                />
                                <button
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                    className={`
                                        relative overflow-hidden group px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-all shadow-lg shadow-orange-500/25 flex items-center justify-center gap-2
                                        ${isGenerating ? 'opacity-80 cursor-not-allowed' : 'active:scale-95'}
                                    `}
                                >
                                    {isGenerating ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Generating...</span>
                                        </>
                                    ) : (
                                        'Generate'
                                    )}
                                </button>
                            </div>

                            <div className="w-64 space-y-6">
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Duration:</label>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => {
                                                setIsCustomDuration(false);
                                                setDuration(Math.ceil(videoDuration || 30));
                                            }}
                                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all ${!isCustomDuration
                                                ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-black border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-orange-500/50'
                                                }`}
                                        >
                                            Match Video ({Math.ceil(videoDuration || 30)}s)
                                        </button>
                                        <button
                                            onClick={() => setIsCustomDuration(true)}
                                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all ${isCustomDuration
                                                ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-black border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-orange-500/50'
                                                }`}
                                        >
                                            Custom
                                        </button>
                                    </div>

                                    {isCustomDuration && (
                                        <div className="flex items-center gap-2 pt-2">
                                            <input
                                                type="number"
                                                min="1"
                                                max="600"
                                                step="0.1"
                                                value={duration}
                                                onChange={(e) => setDuration(parseFloat(e.target.value) || 1)}
                                                className="w-20 bg-white dark:bg-black border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                                            />
                                            <span className="text-xs text-gray-500">seconds</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-start gap-3 group cursor-pointer" onClick={() => setOptimizePrompt(!optimizePrompt)}>
                                    <div className={`mt-1 w-4 h-4 rounded border flex items-center justify-center transition-colors ${optimizePrompt ? 'bg-orange-500 border-orange-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                        {optimizePrompt && <X className="w-3 h-3 text-white rotate-45" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-bold text-gray-900 dark:text-white">Optimize Prompt</div>
                                        <div className="text-[11px] text-gray-500 leading-relaxed">Uses AI to enhance your description with brand context</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Add From Media Library */}
                    <div className="space-y-4">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Add From Media Library</h2>
                        <button
                            onClick={onAddFromDrive}
                            className="w-48 bg-gray-50 dark:bg-[#191919] border border-gray-100 dark:border-white/5 rounded-2xl p-6 flex flex-col items-start gap-4 hover:shadow-lg hover:border-orange-500/20 transition-all group"
                        >
                            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                                <Cloud className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 group-hover:text-orange-500">Add From Media Library</span>
                        </button>
                    </div>

                    {/* Music Library */}
                    <div className="space-y-6">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Music Library</h2>

                        {/* Filters */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <span className="text-xs font-bold text-gray-400 uppercase w-12">Genre:</span>
                                <div className="flex flex-wrap gap-2">
                                    {genres.map(genre => (
                                        <button
                                            key={genre}
                                            onClick={() => setSelectedGenre(genre)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedGenre === genre
                                                ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-black border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                                                }`}
                                        >
                                            {genre} {selectedGenre === genre && <X className="inline-block w-3 h-3 ml-1" />}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <span className="text-xs font-bold text-gray-400 uppercase w-12">Mood:</span>
                                <div className="flex flex-wrap gap-2">
                                    {moods.map(mood => (
                                        <button
                                            key={mood}
                                            onClick={() => setSelectedMood(mood)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedMood === mood
                                                ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-black border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                                                }`}
                                        >
                                            {mood}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Tracks Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {isLoadingAssets ? (
                                <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                                    <p>Loading library assets...</p>
                                </div>
                            ) : assets.length === 0 ? (
                                <div className="col-span-full text-center py-12 text-gray-500 bg-gray-50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
                                    No music files found in your library.
                                </div>
                            ) : (
                                assets.map(track => (
                                    <div
                                        key={track._id}
                                        className={`group bg-gray-50 dark:bg-[#191919] border ${playingTrackId === track._id ? 'border-orange-500 shadow-md' : 'border-gray-100 dark:border-white/5'} rounded-2xl p-4 flex items-center gap-4 hover:shadow-lg hover:border-orange-500/20 transition-all cursor-pointer`}
                                        onClick={() => {
                                            if (onSelectTrack) {
                                                onSelectTrack({
                                                    id: track._id,
                                                    name: track.name,
                                                    url: track.url,
                                                    type: track.mimeType || 'audio/mpeg',
                                                    duration: track.duration || 30
                                                });
                                            }
                                            if (onClose) onClose();
                                        }}
                                    >
                                        <button
                                            onClick={(e) => togglePlay(e, track)}
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transition-transform ${playingTrackId === track._id ? 'bg-orange-600 scale-105 shadow-orange-500/30' : 'bg-orange-500 shadow-orange-500/10 group-hover:scale-105'}`}
                                        >
                                            {loadingTrackId === track._id ? (
                                                <Loader2 className="w-4 h-4 text-white animate-spin" />
                                            ) : playingTrackId === track._id ? (
                                                <Pause className="w-4 h-4 text-white fill-white" />
                                            ) : (
                                                <Play className="w-4 h-4 text-white fill-white" />
                                            )}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate cursor-pointer" title={track.name}>{track.name}</div>
                                            <div className="text-xs text-gray-500 truncate">{track.mimeType}</div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelectTrack && onSelectTrack({
                                                    id: track._id,
                                                    name: track.name,
                                                    url: track.url,
                                                    type: track.mimeType || 'audio/mpeg',
                                                    duration: track.duration || 30
                                                });
                                                onClose();
                                            }}
                                            className="px-3 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 dark:bg-orange-500/10 dark:hover:bg-orange-500/20 rounded-lg text-xs font-semibold transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            Add
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MusicLibraryModal;
